import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface AdminUser {
  id: string;
  auth_user_id: string;
  name: string;
  login: string;
  email: string | null;
  is_active: boolean;
  is_master: boolean;
  created_at: string;
  updated_at: string;
}

interface AdminAuthContextType {
  admin: AdminUser | null;
  loading: boolean;
  isReady: boolean;
  sessionExpired: boolean;
  inactivityExpired: boolean;
  setAdmin: (admin: AdminUser | null) => void;
  clearSessionExpired: () => void;
  clearInactivityExpired: () => void;
}

const INACTIVITY_TIMEOUT_MS = 180 * 60 * 1000;
const MAX_AUTH_FAILURES = 3;
const AUTH_DEBOUNCE_MS = 300;

const AdminAuthContext = createContext<AdminAuthContextType>({
  admin: null,
  loading: true,
  isReady: false,
  sessionExpired: false,
  inactivityExpired: false,
  setAdmin: () => {},
  clearSessionExpired: () => {},
  clearInactivityExpired: () => {},
});

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [inactivityExpired, setInactivityExpired] = useState(false);
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const adminRef = useRef<AdminUser | null>(null);
  const authFailures = useRef(0);
  const processingAuth = useRef(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep ref in sync
  useEffect(() => { adminRef.current = admin; }, [admin]);

  const clearSessionExpired = useCallback(() => setSessionExpired(false), []);
  const clearInactivityExpired = useCallback(() => setInactivityExpired(false), []);

  // Fetch admin user data — extracted to avoid async inside onAuthStateChange
  const fetchAndSetAdmin = useCallback(async (authUserId: string, mounted: { current: boolean }) => {
    if (processingAuth.current) return;
    processingAuth.current = true;
    try {
      const { data } = await supabase
        .from("admin_users")
        .select("*")
        .eq("auth_user_id", authUserId)
        .eq("is_active", true)
        .single();
      if (!mounted.current) return;
      if (data) {
        authFailures.current = 0;
        setAdmin(data as AdminUser);
      } else {
        authFailures.current++;
        if (authFailures.current >= MAX_AUTH_FAILURES) {
          console.warn("[AdminAuth] Max failures reached, signing out.");
          setAdmin(null);
          setSessionExpired(true);
          await supabase.auth.signOut();
        }
      }
    } catch {
      if (!mounted.current) return;
      authFailures.current++;
      if (authFailures.current >= MAX_AUTH_FAILURES) {
        console.warn("[AdminAuth] Max failures reached, signing out.");
        setAdmin(null);
        setSessionExpired(true);
        await supabase.auth.signOut();
      }
    } finally {
      processingAuth.current = false;
    }
  }, []);

  // ── Inactivity timer ──
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    // Only set timer if user is logged in
    if (!adminRef.current) return;
    inactivityTimer.current = setTimeout(async () => {
      if (adminRef.current) {
        setInactivityExpired(true);
        // Sign out after showing message (DashboardLayout handles the 3s delay)
        await supabase.auth.signOut();
        setAdmin(null);
      }
    }, INACTIVITY_TIMEOUT_MS);
  }, []);

  // Track user activity to reset the inactivity timer
  useEffect(() => {
    if (!admin) {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      return;
    }

    const activityEvents = ["mousedown", "keydown", "scroll", "touchstart", "click"];

    const handleActivity = () => resetInactivityTimer();

    // Start the timer
    resetInactivityTimer();

    // Listen for user interactions
    activityEvents.forEach(evt => window.addEventListener(evt, handleActivity, { passive: true }));

    return () => {
      activityEvents.forEach(evt => window.removeEventListener(evt, handleActivity));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, [admin, resetInactivityTimer]);

  // ── Auth session management ──
  useEffect(() => {
    const mounted = { current: true };

    // 1. Restore session from storage first
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!mounted.current) return;
      if (session?.user) {
        await fetchAndSetAdmin(session.user.id, mounted);
      } else {
        if (mounted.current) setAdmin(null);
      }
      if (mounted.current) {
        setLoading(false);
        setIsReady(true);
      }
    });

    // 2. Listen for auth state changes (token refresh, sign out, etc.)
    // IMPORTANT: No awaits inside callback to avoid blocking the event queue
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted.current) return;

        if (event === "SIGNED_OUT" || (event === "TOKEN_REFRESHED" && !session)) {
          const wasLoggedIn = adminRef.current !== null;
          setAdmin(null);
          authFailures.current = 0;
          if (wasLoggedIn) {
            setSessionExpired(true);
          }
          return;
        }

        // Debounce admin user fetch to avoid rapid-fire queries
        if (session?.user && !processingAuth.current) {
          if (debounceTimer.current) clearTimeout(debounceTimer.current);
          debounceTimer.current = setTimeout(() => {
            fetchAndSetAdmin(session.user.id, mounted);
          }, AUTH_DEBOUNCE_MS);
        }
      }
    );

    return () => {
      mounted.current = false;
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      subscription.unsubscribe();
    };
  }, [fetchAndSetAdmin]);

  return (
    <AdminAuthContext.Provider value={{
      admin, loading, isReady, sessionExpired, inactivityExpired,
      setAdmin, clearSessionExpired, clearInactivityExpired,
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  return useContext(AdminAuthContext);
}
